<template>
  <div>
    <!-- Desktop: hamburger shown when menu is collapsed -->
    <button v-if="!isVisible && !isMobile" @click="$emit('toggleMenu')" class="hamburger-btn">
      <i class="bi bi-list"></i>
    </button>

    <!-- Боковое меню с анимацией -->
    <transition name="slide" v-if="configData && configData.menu">
      <div class="side-menu" v-show="isVisible">

        <!-- Menu Header: Logo + Close button -->
        <div class="side-menu__header">
          <TextLogoComponent size="medium" link-to="/" />

          <button @click="$emit('toggleMenu')" class="side-menu__close-btn" :title="$t('menu.hide_menu')">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <!-- Scrollable Menu Content -->
        <div class="side-menu__content">
          <router-link to="/" class="menu-item" v-if="configData.menu.main">
            <i class="bi bi-house-door"></i>
            <span>{{ $t('menu.home') }}</span>
          </router-link>

          <router-link to="/history" class="menu-item" v-if="configData.menu.history">
            <i class="bi bi-clock-history"></i>
            <span>{{ $t('menu.history') }}</span>
          </router-link>

          <router-link to="/materials/filter" class="menu-item" v-if="(configData.chatbot_name === 'chaitanyaacademy' || configData.chatbot_name === 'harikatha')">
            <i class="bi bi-funnel"></i>
            <span>{{ $t('materials.search_placeholder') }}</span>
          </router-link>

          <router-link to="/materials/article-generator" class="menu-item" v-if="configData.chatbot_name === 'chaitanyaacademy'">
            <i class="bi bi-chat-quote"></i>
            <span>{{ $t('menu.gurudev_says') }}</span>
          </router-link>

          <router-link to="/shlokas" class="menu-item" v-if="configData.is_moderator && (configData.chatbot_name === 'chaitanyaacademy' || configData.chatbot_name === 'harikatha')">
            <i class="bi bi-book"></i>
            <span>{{ $t('menu.manage_shlokas') }}</span>
          </router-link>

          <div v-if="!configData.block_data_management">
            <router-link to="/channels" class="menu-item" v-if="configData.menu.channels">
              <i class="bi bi-collection"></i>
              <span>{{ configData.menu.channels.caption }}</span>
            </router-link>

            <router-link to="/collections" class="menu-item" v-if="configData.menu.collections">
              <i class="bi bi-boxes"></i>
              <span>{{ configData.menu.collections.caption }}</span>
            </router-link>

            <!-- Materials (не для meetscribe) -->
            <router-link to="/materials" class="menu-item" v-if="configData.menu.materials && configData.service_name !== 'meetscribe'">
              <i class="bi bi-journal"></i>
              <span>{{ configData.menu.materials.caption }}</span>
            </router-link>

            <!-- Meetings (только для meetscribe) -->
            <router-link to="/meetings" class="menu-item" v-if="configData.service_name === 'meetscribe'">
              <i class="bi bi-calendar-event"></i>
              <span>{{ $t('menu.meetings') || 'Мои встречи' }}</span>
            </router-link>
          </div>

          <!-- MeetScribe: Developer Section -->
          <div class="menu-section-divider" v-if="configData.service_name === 'meetscribe'">
            <span class="menu-section-title">{{ $t('menu.for_developers') }}</span>
          </div>

          <router-link to="/api-settings" class="menu-item" v-if="configData.service_name === 'meetscribe'">
            <i class="bi bi-speedometer2"></i>
            <span>Dashboard</span>
          </router-link>

          <router-link to="/api-docs" class="menu-item" v-if="configData.service_name === 'meetscribe'">
            <i class="bi bi-code-slash"></i>
            <span>API</span>
          </router-link>

          <!-- Subscription Section (MeetScribe, ClipCut, Vid2Text) -->
          <div class="menu-section-divider" v-if="supportsSubscriptions && isAuthenticated">
            <span class="menu-section-title">{{ $t('menu.subscription') }}</span>
          </div>

          <router-link to="/subscription" class="menu-item subscription-menu-item" v-if="supportsSubscriptions && isAuthenticated">
            <SubscriptionBadge
              :chatbot-name="subscriptionChatbotName"
              :show-usage="true"
              :clickable="false"
              :compact="isMobile"
            />
          </router-link>

          <!-- Balance + Top-up Button (always visible for authenticated users) -->
          <div class="balance-topup-row" v-if="supportsSubscriptions && isAuthenticated">
            <div class="balance-display">
              <i class="bi bi-wallet2"></i>
              <span class="balance-amount">{{ formattedBalance }} ₽</span>
            </div>
            <button class="topup-btn" @click="showTopupModal = true">
              <i class="bi bi-plus-circle"></i>
              <span>{{ $t('subscription.topup') }}</span>
            </button>
          </div>

          <!-- Topup Modal -->
          <TopupModal v-model="showTopupModal" :chatbot-name="subscriptionChatbotName" />

          <!-- Upload File Button (для clipcut и vid2text) -->
          <button
            v-if="showUploadButton && isAuthenticated"
            class="menu-item upload-file-btn"
            @click="showUploadModal = true"
          >
            <i class="bi bi-cloud-arrow-up"></i>
            <span>{{ $t('menu.upload_file') }}</span>
          </button>

          <!-- Upload File Modal -->
          <UploadFileModal
            :show="showUploadModal"
            @close="showUploadModal = false"
            @uploaded="handleUploadSuccess"
          />

          <telegram-auth class="mb-3" v-if="!configData.disable_telegram_auth"></telegram-auth>
        </div>

        <!-- Settings Footer: Language + Theme + Copyright -->
        <div class="side-menu__footer">
          <div class="settings-row">
            <!-- Language Switcher -->
            <div class="language-toggle">
              <button
                @click="setLanguage('ru')"
                :class="['lang-btn', { active: currentLocale === 'ru' }]"
                title="Русский"
              >
                RU
              </button>
              <button
                @click="setLanguage('en')"
                :class="['lang-btn', { active: currentLocale === 'en' }]"
                title="English"
              >
                EN
              </button>
            </div>
            <!-- Theme Toggle -->
            <button @click="toggleTheme" class="theme-toggle-btn" :title="darkMode ? $t('theme.switch_to_light') : $t('theme.switch_to_dark')">
              <i :class="darkMode ? 'bi bi-sun-fill' : 'bi bi-moon-fill'"></i>
            </button>
          </div>
          <!-- Copyright & Support -->
          <div class="footer-info">
            <span class="copyright" v-html="configData.footer_text"></span>
            <a href="https://t.me/ayarmots" target="_blank" rel="noopener" class="support-link">
              <i class="bi bi-telegram"></i> {{ $t('menu.support') }}
            </a>
          </div>
        </div>
      </div>
    </transition>

    <overlay-component :visible="isVisible && isMobile" @click="$emit('toggleMenu')"></overlay-component>
  </div>
</template>


<script>
import { computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import { mapGetters, mapActions } from 'vuex';
import OverlayComponent from '@/components/OverlayComponent.vue';
import TextLogoComponent from '@/components/TextLogoComponent.vue';
import SubscriptionBadge from '@/components/subscription/SubscriptionBadge.vue';
import TopupModal from '@/components/subscription/TopupModal.vue';
import UploadFileModal from '@/components/UploadFileModal.vue';
import useClipboard from 'vue-clipboard3'
import { useToast } from "vue-toastification";
import TelegramAuth from './TelegramAuth.vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

export default {
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    }
  },

  data(){
    return{
      menuExpanded: this.isVisible, // Управляет состоянием меню
      showTopupModal: false,
      showUploadModal: false
    }
  },

  components: {
    OverlayComponent,
    TelegramAuth,
    TextLogoComponent,
    SubscriptionBadge,
    TopupModal,
    UploadFileModal
  },  

  watch: {
    // Следим за изменениями параметра isVisible
    isVisible(newVal) {
      this.menuExpanded = newVal;
    }
  },  
  methods: {
    ...mapActions('app', ['setIsMobile', 'toggleDarkMode']),

    toggleTheme() {
      this.toggleDarkMode();
    },

    handleUploadSuccess(data) {
      // Можно перенаправить на страницу материала или обновить список
      if (data && data.materialId) {
        this.$router.push(`/app/article/${data.materialId}`);
      }
      // Закрываем меню на мобильных
      if (this.isMobile) {
        this.$emit('toggleMenu');
      }
    },

  },
  mounted() {
    // Устанавливаем класс при монтировании компонента

    nextTick(() => {
      console.log('isMobile ', this.isMobile);
      console.log('configData in SideMenu:', this.configData);
      console.log('chatbot_name:', this.configData?.chatbot_name);
      if (this.isMobile){
        this.menuExpanded = false;
        console.log(this.menuExpanded);
      } else {
        this.menuExpanded = true;
      }


    });
  },
  computed: {
    ...mapGetters('app', ['isMobile', 'configData', 'darkMode']),
    ...mapGetters("article", ["getArticleItem"]),
    ...mapGetters("channel", ["getChannelItem"]),
    ...mapGetters("collection", ["getCollectionItem"]),

    isClipCut() {
      return this.configData?.chatbot_name === 'clipcut' ||
             this.configData?.chatbot_name === 'clipcutglobal' ||
             this.configData?.service_name === 'clipcut' ||
             this.configData?.service_name === 'clipcutglobal';
    },

    articleItem: {
      get() {
        return this.getArticleItem || {};
      },
      set(value) {
        this.updateArticleItem(value);
      },
    },
    channelItem: {
      get() {
        return this.getChannelItem || {};
      },
      set(value) {
        this.updateChannelItem(value);
      },
    },
    collectionItem: {
      get() {
        return this.getCollectionItem || {};
      },
      set(value) {
        this.updateCollectionItem(value);
      },
    }
  },
  setup() {
    const route = useRoute();
    const store = useStore();
    const { locale } = useI18n();
    const article = computed(() => store.getters['article/getArticleItem']);
    const collection = computed(() => store.getters['collection/getCollectionItem']);
    const isAuthenticated = computed(() => store.getters['user/isAuthenticated']);
    const isFreePlan = computed(() => store.getters['subscription/isFreePlan']);
    const getTopupUrl = computed(() => store.getters['subscription/getTopupUrl']);
    const getBalance = computed(() => store.getters['subscription/getBalance']);

    // Formatted balance for display
    const formattedBalance = computed(() => {
      const balance = getBalance.value || 0;
      return balance.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    });

    // Top-up URL with fallback
    const topupUrl = computed(() => {
      return getTopupUrl.value || 'https://bystropay.ru/payment.php?product=topup&chatbot_name=meetscribe';
    });

    // Check if current service supports subscriptions
    const configData = computed(() => store.getters['app/configData']);
    const supportsSubscriptions = computed(() => {
      const serviceName = configData.value?.service_name;
      const chatbotName = configData.value?.chatbot_name;
      return ['meetscribe', 'clipcut', 'clipcutglobal', 'vid2text'].includes(serviceName) ||
             ['meetscribe', 'clipcut', 'clipcutglobal', 'vid2text'].includes(chatbotName);
    });

    // Get chatbot name for subscription API calls
    const subscriptionChatbotName = computed(() => {
      const chatbotName = configData.value?.chatbot_name;
      // Normalize clipcutglobal to clipcut for subscription purposes
      if (chatbotName === 'clipcutglobal') return 'clipcut';
      return chatbotName || 'meetscribe';
    });

    // Show upload button for clipcut and vid2text (not meetscribe - has its own upload in dashboard)
    const showUploadButton = computed(() => {
      const serviceName = configData.value?.service_name;
      const chatbotName = configData.value?.chatbot_name;
      // Show for clipcut, clipcutglobal, vid2text, narezkavideo
      // NOT for meetscribe (has its own upload in MeetingsDashboard)
      const uploadServices = ['clipcut', 'clipcutglobal', 'vid2text', 'narezkavideo'];
      return uploadServices.includes(serviceName) || uploadServices.includes(chatbotName);
    });

    const { toClipboard } = useClipboard()

    const { t } = useI18n();

    const copy = async (text) => {
      try {
        await toClipboard(text)
        const toast = useToast();
        toast.success(t('article.toasts.copied_to_clipboard'), { timeout: 2000 });
      } catch (e) {
        console.error(e)
      }
    }

    const currentLocale = computed(() => locale.value);

    const setLanguage = (lang) => {
      locale.value = lang;
      localStorage.setItem('user-locale', lang);
    };

    return {
      article, collection, copy, isAuthenticated, route, currentLocale, setLanguage, isFreePlan, topupUrl, formattedBalance,
      supportsSubscriptions, subscriptionChatbotName, showUploadButton
    };
  } 
};
</script>

<style scoped>
/* =====================================================
   Side Menu - Flexbox Layout with Scrollable Content
   ===================================================== */
.side-menu {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  height: 100dvh; /* Dynamic viewport height for mobile */
  width: 300px;
  display: flex;
  flex-direction: column;
  background-color: var(--yt-bg-secondary, #f8f9fa);
  box-shadow: var(--yt-shadow-md, 2px 0 5px rgba(0, 0, 0, 0.1));
  z-index: 99;
  transition: transform 0.3s ease, background-color 0.3s ease;
  overflow: hidden;
}

.isMobile .side-menu {
  z-index: 101;
  width: 85vw;
  max-width: 320px;
}

/* Menu Header: Logo + Close Button */
.side-menu__header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--yt-border-color, #e5e7eb);
}

.side-menu__logo {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.side-menu__logo img {
  max-height: 40px;
  max-width: 180px;
  object-fit: contain;
}

.side-menu__logo-placeholder {
  height: 40px;
}

.side-menu__close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background-color: var(--yt-bg-tertiary, #f3f4f6);
  border-radius: 8px;
  color: var(--yt-text-secondary, #6b7280);
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.side-menu__close-btn:hover {
  background-color: var(--yt-danger, #ef4444);
  color: white;
}

/* Scrollable Menu Content */
.side-menu__content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  -webkit-overflow-scrolling: touch;
}

/* Custom scrollbar for menu content */
.side-menu__content::-webkit-scrollbar {
  width: 6px;
}

.side-menu__content::-webkit-scrollbar-track {
  background: transparent;
}

.side-menu__content::-webkit-scrollbar-thumb {
  background-color: var(--yt-border-color, #d1d5db);
  border-radius: 3px;
}

.side-menu__content::-webkit-scrollbar-thumb:hover {
  background-color: var(--yt-text-muted, #9ca3af);
}

/* Menu Footer: Language, Theme, Copyright */
.side-menu__footer {
  flex-shrink: 0;
  padding: 16px 20px;
  border-top: 1px solid var(--yt-border-color, #e5e7eb);
  background-color: var(--yt-bg-secondary, #f8f9fa);
}

/* =====================================================
   Menu Items
   ===================================================== */
.side-menu .menu-item {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  margin-bottom: 8px;
  color: var(--yt-text-primary, #333);
  font-weight: 500;
  text-decoration: none;
  background-color: var(--yt-bg-card, #fff);
  border-radius: 8px;
  transition: background-color 0.2s, color 0.2s;
}

.side-menu .menu-item i {
  font-size: 20px;
  margin-right: 10px;
  color: var(--yt-text-secondary, #6c757d);
}

.side-menu .menu-item span {
  font-size: 15px;
}

.side-menu .menu-item:hover {
  background-color: var(--yt-hover-bg, #e7f3ff);
  color: var(--yt-primary, #007bff);
}

.side-menu .menu-item:hover i {
  color: var(--yt-primary, #007bff);
}

.side-menu .menu-item-active {
  background-color: var(--yt-active-bg, #e0f0ff);
  color: var(--yt-primary, #007bff);
}

/* Telegram Auth in menu */
.side-menu .telegram-auth {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  justify-content: center;
  margin-bottom: 8px;
  color: var(--yt-text-primary, #333);
  font-weight: 500;
  text-decoration: none;
  background-color: var(--yt-bg-card, #fff);
  border-radius: 8px;
  transition: background-color 0.2s, color 0.2s;
}

/* =====================================================
   Desktop Hamburger Button
   ===================================================== */
.hamburger-btn {
  position: fixed;
  top: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: var(--yt-bg-card, #fff);
  border: 1px solid var(--yt-border-color, #e5e7eb);
  border-radius: 8px;
  font-size: 24px;
  color: var(--yt-text-primary, #1f2937);
  cursor: pointer;
  z-index: 48;
  box-shadow: var(--yt-shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.05));
  transition: all 0.2s ease;
}

.hamburger-btn:hover {
  background-color: var(--yt-hover-bg, #f3f4f6);
  border-color: var(--yt-primary, #4F46E5);
}

/* =====================================================
   Slide Animation
   ===================================================== */
.slide-enter-active, .slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from, .slide-leave-to {
  transform: translateX(-100%);
}

.settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: var(--yt-bg-card, #fff);
  border-radius: 8px;
  border: 1px solid var(--yt-border-color, #e5e7eb);
}

.language-toggle {
  display: flex;
  gap: 4px;
}

.lang-btn {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid var(--yt-border-color, #e5e7eb);
  background-color: transparent;
  color: var(--yt-text-secondary, #6b7280);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.lang-btn:hover {
  background-color: var(--yt-hover-bg, #e7f3ff);
  color: var(--yt-primary, #4F46E5);
  border-color: var(--yt-primary, #4F46E5);
}

.lang-btn.active {
  background-color: var(--yt-primary, #4F46E5);
  color: white;
  border-color: var(--yt-primary, #4F46E5);
}

.theme-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background-color: var(--yt-bg-tertiary, #e9ecef);
  border-radius: 8px;
  color: var(--yt-text-secondary, #6b7280);
  cursor: pointer;
  transition: all 0.2s ease;
}

.theme-toggle-btn:hover {
  background-color: var(--yt-primary, #4F46E5);
  color: white;
  transform: scale(1.05);
}

.theme-toggle-btn i {
  font-size: 18px;
}

/* Logo adaptation for dark theme */
.side-menu__logo img,
.side-menu .menu-item img {
  transition: filter 0.3s ease;
}

html[data-bs-theme="dark"] .side-menu__logo img,
html[data-bs-theme="dark"] .side-menu .menu-item img {
  filter: brightness(0) invert(1);
}

/* Menu section divider */
.menu-section-divider {
  margin: 15px 0 10px 0;
  padding: 0 15px;
  border-top: 1px solid var(--yt-border-color, #e5e7eb);
  padding-top: 15px;
}

.menu-section-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--yt-text-muted, #9ca3af);
}

/* Footer info (copyright + support) */
.footer-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--yt-border-color, #e5e7eb);
  font-size: 12px;
  color: var(--yt-text-muted, #9ca3af);
}

.footer-info .copyright {
  text-align: center;
}

.footer-info .copyright :deep(a) {
  color: var(--yt-primary, #4F46E5);
  text-decoration: none;
}

.footer-info .copyright :deep(a:hover) {
  text-decoration: underline;
}

.footer-info .support-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--yt-text-muted, #9ca3af);
  text-decoration: none;
  transition: color 0.2s;
}

.footer-info .support-link:hover {
  color: var(--yt-primary, #4F46E5);
}

/* Subscription menu item */
.subscription-menu-item {
  padding: 8px 12px !important;
  justify-content: center;
}

.subscription-menu-item:hover {
  background-color: var(--yt-hover-bg, #e7f3ff) !important;
}

/* Balance + Top-up Row */
.balance-topup-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  margin-bottom: 8px;
  background-color: var(--yt-bg-card, #fff);
  border-radius: 8px;
  border: 1px solid var(--yt-border-color, #e5e7eb);
}

.balance-display {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  color: var(--yt-text-primary, #1f2937);
  font-weight: 600;
  font-size: 15px;
}

.balance-display i {
  color: #10B981;
  font-size: 18px;
}

.balance-amount {
  color: var(--yt-text-primary, #1f2937);
}

.topup-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.topup-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.topup-btn i {
  font-size: 14px;
}

/* Dark mode for balance row */
html[data-bs-theme="dark"] .balance-topup-row {
  background-color: var(--yt-bg-tertiary, #374151);
  border-color: var(--yt-border-color, #4B5563);
}

html[data-bs-theme="dark"] .balance-display {
  color: var(--yt-text-primary, #F9FAFB);
}

html[data-bs-theme="dark"] .balance-amount {
  color: var(--yt-text-primary, #F9FAFB);
}

/* Upload file button */
.upload-file-btn {
  border: 1px dashed var(--yt-primary, #4F46E5) !important;
  background-color: rgba(79, 70, 229, 0.05) !important;
  color: var(--yt-primary, #4F46E5) !important;
  cursor: pointer;
  width: 100%;
  text-align: left;
}

.upload-file-btn i {
  color: var(--yt-primary, #4F46E5) !important;
}

.upload-file-btn:hover {
  background-color: rgba(79, 70, 229, 0.1) !important;
  border-style: solid !important;
}

html[data-bs-theme="dark"] .upload-file-btn {
  background-color: rgba(79, 70, 229, 0.1) !important;
}

html[data-bs-theme="dark"] .upload-file-btn:hover {
  background-color: rgba(79, 70, 229, 0.2) !important;
}
</style>
